/**
 * Shared story projection for Scene View ↔ Editor View ↔ Workflow View.
 * Source of truth = project scenes/segments from the API.
 * Workflow graph is a derived representation; content edits patch back to scenes.
 */
import api from '@/lib/api';
import { narrationSegmentsFromProject, narrationProjectToStory } from '@/lib/sceneGridNarrationAdapter';
import { collectSceneMediaVersions } from '@/lib/sceneMediaVersions';
import {
  fetchNarrationSceneEditShell,
  fetchNarrationSceneEditScenesPage,
  mapEditSegmentsToScenes,
} from '@/lib/narrationSceneEditLoad';

export function workflowLayoutStorageKey(mode, id) {
  return `twinnee-workflow-layout-v5:${mode}:${id || 'draft'}`;
}

export function readWorkflowLayout(mode, id) {
  try {
    const raw = localStorage.getItem(workflowLayoutStorageKey(mode, id));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function writeWorkflowLayout(mode, id, layout) {
  try {
    localStorage.setItem(
      workflowLayoutStorageKey(mode, id),
      JSON.stringify(layout || {}),
    );
  } catch {
    /* ignore */
  }
}

export function workflowUserEdgesStorageKey(mode, id) {
  return `twinnee-workflow-user-edges-v1:${mode}:${id || 'draft'}`;
}

export function readWorkflowUserEdges(mode, id) {
  try {
    const raw = localStorage.getItem(workflowUserEdgesStorageKey(mode, id));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeWorkflowUserEdges(mode, id, edges) {
  try {
    localStorage.setItem(
      workflowUserEdgesStorageKey(mode, id),
      JSON.stringify(Array.isArray(edges) ? edges : []),
    );
  } catch {
    /* ignore */
  }
}

/** Strip obsolete content-only workflow graphs from v1 localStorage. */
export function clearLegacyWorkflowGraph(mode, id) {
  try {
    localStorage.removeItem(`twinnee-workflow-v1:${mode}:${id || 'draft'}`);
  } catch {
    /* ignore */
  }
}

async function fetchAllNarrationEditScenes(projectId, project) {
  const all = [];
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const page = await fetchNarrationSceneEditScenesPage(projectId, {
      limit: 60,
      offset,
    });
    all.push(...mapEditSegmentsToScenes(page.segments, project));
    hasMore = page.has_more && page.segments.length > 0;
    offset = page.offset + page.segments.length;
    if (offset > 2000) break;
  }
  return all;
}

export async function loadStoryRepresentation(mode, id) {
  if (!id) throw new Error('Missing project id');

  if (mode === 'narration') {
    // Shell + lean pages — avoid fat GET /projects/{id} timeouts.
    const project = await fetchNarrationSceneEditShell(id);
    const scenes = await fetchAllNarrationEditScenes(id, project);
    return {
      mode: 'narration',
      id,
      title: project?.title || project?.input?.topic || 'Narration',
      story: narrationProjectToStory(project),
      scenes: scenes.length ? scenes : narrationSegmentsFromProject({ ...project, segments: [] }),
      project: { ...project, segments: scenes },
    };
  }

  const [{ data: story }, { data: scenes }] = await Promise.all([
    api.get(`/stories/${id}`),
    api.get(`/stories/${id}/scenes`, { params: { _fresh: Date.now() } }),
  ]);
  return {
    mode: 'story',
    id,
    title: story?.title || 'Story',
    story,
    scenes: Array.isArray(scenes) ? scenes : [],
    project: null,
  };
}

function sceneLabel(scene, index) {
  const n = scene?.scene_number ?? scene?.segment_number ?? index + 1;
  const title = String(scene?.scene_title || scene?.title || '').trim();
  return title ? `Scene ${n} · ${title}` : `Scene ${n}`;
}

function sceneScript(scene) {
  return String(
    scene?.scene_text || scene?.narration_text || scene?.text || scene?.authored_script || '',
  ).trim();
}

function sceneVisual(scene) {
  return String(
    scene?.visual || scene?.image_prompt || scene?.camera_direction || '',
  ).trim();
}

function pos(layout, nodeId, fallback) {
  const p = layout?.[nodeId];
  if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return { x: p.x, y: p.y };
  return fallback;
}

function versionNodeId(kind, sceneId, url, idx) {
  const slug = String(url || `empty_${idx}`)
    .split('')
    .reduce((acc, ch) => ((acc * 31 + ch.charCodeAt(0)) >>> 0), 7)
    .toString(36);
  return `scene_${kind}_${sceneId}_v${idx}_${slug}`;
}

function pushVersionNodes({
  nodes,
  edges,
  versions,
  kind,
  sceneId,
  sceneLabelText,
  baseX,
  baseY,
  layout,
  connectFromScriptId,
  activeVideoIdRef,
  activeImageIdRef,
  activeAudioIdRef,
}) {
  const VERSION_GAP_Y = 168;
  const list = versions.length
    ? versions
    : [{ url: null, isActive: true, source: 'empty' }];

  list.forEach((ver, vIdx) => {
    const id = versionNodeId(kind, sceneId, ver.url, vIdx);
    const y = baseY + vIdx * VERSION_GAP_Y;
    const isActive = Boolean(ver.isActive);
    const n = (vIdx + 1);
    const total = list.length;

    if (kind === 'image') {
      nodes.push({
        id,
        type: 'workflowNode',
        position: pos(layout, id, { x: baseX, y }),
        data: {
          kind: 'image',
          label: `${sceneLabelText} · Image ${n}/${total}`,
          text: ver.source ? `source: ${ver.source}` : 'Image version',
          imageUrl: ver.url || null,
          mediaUrl: ver.url || null,
          sceneId,
          field: 'image_version',
          mediaKind: 'image',
          mediaVersion: true,
          isActive,
          synced: true,
          versionIndex: n,
          versionTotal: total,
        },
      });
      if (isActive) activeImageIdRef.current = id;
    } else if (kind === 'video') {
      nodes.push({
        id,
        type: 'workflowNode',
        position: pos(layout, id, { x: baseX, y }),
        data: {
          kind: 'video',
          label: `${sceneLabelText} · Video ${n}/${total}`,
          text: ver.source ? `source: ${ver.source}` : 'Video version',
          imageUrl: null,
          videoUrl: ver.url || null,
          mediaUrl: ver.url || null,
          sceneId,
          field: 'video_version',
          mediaKind: 'video',
          mediaVersion: true,
          isActive,
          synced: true,
          versionIndex: n,
          versionTotal: total,
          duration: '—',
        },
      });
      if (isActive) activeVideoIdRef.current = id;
    } else {
      nodes.push({
        id,
        type: 'workflowNode',
        position: pos(layout, id, { x: baseX, y }),
        data: {
          kind: 'audio',
          label: `${sceneLabelText} · Audio ${n}/${total}`,
          text: ver.source ? `source: ${ver.source}` : 'Audio version',
          audioUrl: ver.url || null,
          mediaUrl: ver.url || null,
          sceneId,
          field: 'audio_version',
          mediaKind: 'audio',
          mediaVersion: true,
          isActive,
          synced: true,
          versionIndex: n,
          versionTotal: total,
        },
      });
      if (isActive) activeAudioIdRef.current = id;
    }

    // Dim edges from inactive versions; strong edges from active ones into the pipeline
    if (kind === 'image' && connectFromScriptId && isActive) {
      // wired later once we know active video
    }
  });

  return list;
}

/**
 * Horizontal story workflow with every image / video / audio version as its own node.
 * Active (selected) versions are highlighted and drive the main chain.
 */
export function scenesToWorkflowGraph(scenes, { title = 'Story', layout = {} } = {}) {
  const list = Array.isArray(scenes) ? scenes : [];
  const nodes = [];
  const edges = [];

  const NODE_W = 240;
  const COL_GAP = 120;
  const STAGE_GAP = 200;
  // script | images | videos | audios
  const STAGE_W = NODE_W + COL_GAP + NODE_W + COL_GAP + NODE_W + COL_GAP + NODE_W + STAGE_GAP;
  const START_X = 56;
  const CENTER_Y = 260;
  const VERSIONS_TOP_Y = 36;
  const TITLE_TO_FIRST = NODE_W + STAGE_GAP;

  const titleId = 'story_title';
  nodes.push({
    id: titleId,
    type: 'workflowNode',
    position: pos(layout, titleId, { x: START_X, y: CENTER_Y }),
    data: {
      kind: 'text',
      label: 'Story',
      text: title || 'Untitled story',
      synced: true,
      field: 'title',
      readOnly: true,
    },
    draggable: true,
  });

  let prevOutId = titleId;
  let prevOutHandle = 'text';

  list.forEach((scene, index) => {
    const sid = scene?.id || `idx_${index}`;
    const label = sceneLabel(scene, index);
    const stageX = START_X + TITLE_TO_FIRST + index * STAGE_W;
    const scriptX = stageX;
    const imageX = stageX + NODE_W + COL_GAP;
    const videoX = imageX + NODE_W + COL_GAP;
    const audioX = videoX + NODE_W + COL_GAP;

    const textId = `scene_text_${sid}`;
    nodes.push({
      id: textId,
      type: 'workflowNode',
      position: pos(layout, textId, { x: scriptX, y: CENTER_Y }),
      data: {
        kind: 'text',
        label: `${label} · Script`,
        text: sceneScript(scene) || '(empty script)',
        sceneId: sid,
        field: 'script',
        synced: true,
        sceneNumber: scene?.scene_number ?? scene?.segment_number ?? index + 1,
      },
    });

    edges.push({
      id: `e_${prevOutId}_${textId}`,
      source: prevOutId,
      sourceHandle: prevOutHandle,
      target: textId,
      targetHandle: 'in',
      animated: true,
      style: { stroke: '#64748b', strokeWidth: 1.5 },
      type: 'default',
    });

    const imageVersions = collectSceneMediaVersions(scene, 'image');
    const videoVersions = collectSceneMediaVersions(scene, 'video');
    const audioVersions = collectSceneMediaVersions(scene, 'audio');

    const activeImageIdRef = { current: null };
    const activeVideoIdRef = { current: null };
    const activeAudioIdRef = { current: null };

    pushVersionNodes({
      nodes,
      edges,
      versions: imageVersions,
      kind: 'image',
      sceneId: sid,
      sceneLabelText: label,
      baseX: imageX,
      baseY: VERSIONS_TOP_Y,
      layout,
      activeImageIdRef,
      activeVideoIdRef,
      activeAudioIdRef,
    });
    pushVersionNodes({
      nodes,
      edges,
      versions: videoVersions,
      kind: 'video',
      sceneId: sid,
      sceneLabelText: label,
      baseX: videoX,
      baseY: VERSIONS_TOP_Y,
      layout,
      activeImageIdRef,
      activeVideoIdRef,
      activeAudioIdRef,
    });
    pushVersionNodes({
      nodes,
      edges,
      versions: audioVersions,
      kind: 'audio',
      sceneId: sid,
      sceneLabelText: label,
      baseX: audioX,
      baseY: VERSIONS_TOP_Y,
      layout,
      activeImageIdRef,
      activeVideoIdRef,
      activeAudioIdRef,
    });

    const activeImageId = activeImageIdRef.current;
    const activeVideoId = activeVideoIdRef.current;
    const activeAudioId = activeAudioIdRef.current;

    // Script → active video
    if (activeVideoId) {
      edges.push({
        id: `e_${textId}_${activeVideoId}_prompt`,
        source: textId,
        sourceHandle: 'text',
        target: activeVideoId,
        targetHandle: 'prompt',
        animated: true,
        style: { stroke: '#62FAE3', strokeWidth: 2 },
        type: 'default',
      });
    }

    // Active image → active video
    if (activeImageId && activeVideoId) {
      edges.push({
        id: `e_${activeImageId}_${activeVideoId}_image`,
        source: activeImageId,
        sourceHandle: 'image',
        target: activeVideoId,
        targetHandle: 'image',
        animated: true,
        style: { stroke: '#A78BFA', strokeWidth: 2 },
        type: 'default',
      });
    }

    // Active audio → active video (timing / voice)
    if (activeAudioId && activeVideoId) {
      edges.push({
        id: `e_${activeAudioId}_${activeVideoId}_audio`,
        source: activeAudioId,
        sourceHandle: 'audio',
        target: activeVideoId,
        targetHandle: 'prompt',
        animated: true,
        style: { stroke: '#FBBF24', strokeWidth: 1.5, strokeDasharray: '4 3' },
        type: 'default',
      });
    }

    // Chain forward from active video (or script if no video)
    if (activeVideoId) {
      prevOutId = activeVideoId;
      prevOutHandle = 'video';
    } else {
      prevOutId = textId;
      prevOutHandle = 'text';
    }
  });

  const previewId = 'story_preview';
  const previewX = START_X + TITLE_TO_FIRST + list.length * STAGE_W;
  nodes.push({
    id: previewId,
    type: 'workflowNode',
    position: pos(layout, previewId, { x: previewX, y: CENTER_Y }),
    data: {
      kind: 'preview',
      label: 'Story preview',
      text: `${list.length} scene${list.length === 1 ? '' : 's'} · all media versions on canvas`,
      synced: true,
      field: 'preview',
      readOnly: true,
    },
  });

  edges.push({
    id: `e_${prevOutId}_${previewId}`,
    source: prevOutId,
    sourceHandle: prevOutHandle,
    target: previewId,
    targetHandle: 'in',
    animated: true,
    style: { stroke: '#F472B6', strokeWidth: 1.75 },
    type: 'default',
  });

  return { nodes, edges, sceneCount: list.length };
}

export function extractLayoutFromNodes(nodes) {
  const layout = {};
  (nodes || []).forEach((n) => {
    if (n?.id && n.position) layout[n.id] = { x: n.position.x, y: n.position.y };
  });
  return layout;
}

/**
 * Persist a workflow node content edit into the shared scene document.
 */
export async function patchSceneFromWorkflowNode({
  mode,
  projectId,
  nodeData,
  text,
  scenes = [],
}) {
  const sceneId = nodeData?.sceneId;
  const field = nodeData?.field;
  if (!sceneId || !field || nodeData?.readOnly) return null;

  const scene = (scenes || []).find((s) => s.id === sceneId);
  const nextText = String(text ?? '');

  if (mode === 'narration') {
    if (field === 'script') {
      const { data } = await api.patch(`/narration/projects/${projectId}/segments/${sceneId}`, {
        text: nextText,
        visual: scene?.visual || sceneVisual(scene) || nextText,
        emotion: scene?.emotion,
        phoneme_notes: scene?.phoneme_notes || '',
      });
      return data;
    }
    if (field === 'visual') {
      const { data } = await api.patch(`/narration/projects/${projectId}/segments/${sceneId}`, {
        text: sceneScript(scene) || scene?.text || '',
        visual: nextText,
        emotion: scene?.emotion,
        phoneme_notes: scene?.phoneme_notes || '',
      });
      return data;
    }
    return null;
  }

  // Dialogue / story scenes
  if (field === 'script') {
    const { data } = await api.put(`/scenes/${sceneId}`, { scene_text: nextText });
    return data;
  }
  if (field === 'visual') {
    const { data } = await api.put(`/scenes/${sceneId}`, { image_prompt: nextText || null });
    return data;
  }
  return null;
}

/** Apply a patched segment/scene into local scenes array. */
export function mergePatchedScene(scenes, patched, mode) {
  if (!patched?.id) return scenes;
  return (scenes || []).map((s) => {
    if (s.id !== patched.id) return s;
    if (mode === 'narration') {
      return {
        ...s,
        ...patched,
        scene_text: patched.text ?? patched.authored_script ?? s.scene_text,
        text: patched.text ?? s.text,
        visual: patched.visual ?? s.visual,
        image_url: patched.image_url ?? s.image_url,
        video_url: patched.video_url ?? s.video_url,
        audio_url: patched.audio_url ?? s.audio_url,
        image_history: patched.image_history ?? s.image_history,
        video_history: patched.video_history ?? s.video_history,
        audio_history: patched.audio_history ?? s.audio_history,
      };
    }
    return { ...s, ...patched };
  });
}

/**
 * Make a media version the active one for a scene (same API as Scene / Editor views).
 */
export async function selectSceneMediaVersion({ mode, projectId, sceneId, kind, url }) {
  if (!sceneId || !kind || !url) throw new Error('Missing media selection fields');
  if (mode === 'narration') {
    const { data } = await api.post(
      `/narration/projects/${projectId}/segments/${sceneId}/select-media`,
      { kind, url },
    );
    return data;
  }
  const { data } = await api.post(`/scenes/${sceneId}/select-media`, { kind, url });
  return data;
}
